import { openBlobFile } from "@/utils/utils";
import { useMutation } from "@tanstack/react-query";
import { calculatorsApi } from "../../../modules/calculator/calcs/api";
import { RealtyCalc, RealtyCalcResultData, RealtyGen } from "../../../modules/calculator/calcs/realty/types";
import { TravelCalc, TravelCalcResultData, TravelGen } from "../../../modules/calculator/calcs/travel/types";
import { Offer } from "../../../modules/calculator/calcs/types";
import {
  VehicleCalc,
  VehicleCalcResultData,
  VehicleCalcType,
  VehicleGen,
} from "../../../modules/calculator/calcs/vehicle/types";
import { CalcType } from "../../../modules/calculator/enums";

export const CALCULATORS_QUERY_KEYS = {
  calculate: () => ["calculate"],
  calculateVehicle: () => ["calculate", "vehicle"],
  calculateRealty: () => ["calculate", "realty"],
  calculateTravel: () => ["calculate", "travel"],
  generate: () => ["generate"],
  generateVehicle: () => ["generate", "vehicle"],
  generateRealty: () => ["generate", "realty"],
  generateTravel: () => ["generate", "travel"],
  generateOfferVehicle: () => ["generateOffer", "vehicle"],
  generateOfferRealty: () => ["generateOffer", "realty"],
  generateOfferTravel: () => ["generateOffer", "travel"],
  sendOfferVehicle: () => ["sendOffer", "vehicle"],
  sendOfferRealty: () => ["sendOffer", "realty"],
  sendOfferTravel: () => ["sendOffer", "travel"],
  uploadCalcAttachments: () => ["uploadCalcAttachments"],
  downloadCardReader: () => ["downloadCardReader"],
};

export const useVehicleCalculate = () => {
  return useMutation({
    mutationKey: CALCULATORS_QUERY_KEYS.calculateVehicle(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: VehicleCalc) => {
      const { data } = await calculatorsApi.calculate<VehicleCalcResultData, VehicleCalcType>(payload);

      return data.results;
    },
  });
};

export const useRealtyCalculate = () => {
  return useMutation({
    mutationKey: CALCULATORS_QUERY_KEYS.calculateRealty(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: RealtyCalc) => {
      const { data } = await calculatorsApi.calculate<RealtyCalcResultData, CalcType.REALTY>(payload);

      return data.results;
    },
  });
};

export const useTravelCalculate = () => {
  return useMutation({
    mutationKey: CALCULATORS_QUERY_KEYS.calculateTravel(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: TravelCalc) => {
      const { data } = await calculatorsApi.calculate<TravelCalcResultData, CalcType.TRAVEL>(payload);

      return data.results;
    },
  });
};

export const useVehicleGenerate = () => {
  return useMutation({
    mutationKey: CALCULATORS_QUERY_KEYS.generateVehicle(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: VehicleGen) => {
      const { data } = await calculatorsApi.generate(payload);

      return data;
    },
  });
};

export const useRealtyGenerate = () => {
  return useMutation({
    mutationKey: CALCULATORS_QUERY_KEYS.generateRealty(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: RealtyGen) => {
      const { data } = await calculatorsApi.generate(payload);

      return data;
    },
  });
};

export const useTravelGenerate = () => {
  return useMutation({
    mutationKey: CALCULATORS_QUERY_KEYS.generateTravel(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: TravelGen) => {
      const { data } = await calculatorsApi.generate(payload);

      return data;
    },
  });
};

export const useVehicleGenerateOffer = () => {
  return useMutation({
    mutationKey: CALCULATORS_QUERY_KEYS.generateOfferVehicle(),
    mutationFn: async (payload: Offer<VehicleCalc, VehicleCalcResultData>) => {
      const response = await calculatorsApi.generateOffer(payload);
      openBlobFile(response, true);

      return response.data;
    },
  });
};

export const useRealtyGenerateOffer = () => {
  return useMutation({
    mutationKey: CALCULATORS_QUERY_KEYS.generateOfferRealty(),
    mutationFn: async (payload: Offer<RealtyCalc, RealtyCalcResultData>) => {
      const response = await calculatorsApi.generateOffer(payload);
      openBlobFile(response, true);

      return response.data;
    },
  });
};

export const useTravelGenerateOffer = () => {
  return useMutation({
    mutationKey: CALCULATORS_QUERY_KEYS.generateOfferTravel(),
    mutationFn: async (payload: Offer<TravelCalc, TravelCalcResultData>) => {
      const response = await calculatorsApi.generateOffer(payload);
      openBlobFile(response, true);

      return response.data;
    },
  });
};

export const useVehicleSendOffer = () => {
  return useMutation({
    mutationKey: CALCULATORS_QUERY_KEYS.sendOfferVehicle(),
    mutationFn: async (payload: Offer<VehicleCalc, VehicleCalcResultData>) => {
      const { data } = await calculatorsApi.sendOffer(payload);

      return data;
    },
  });
};

export const useRealtySendOffer = () => {
  return useMutation({
    mutationKey: CALCULATORS_QUERY_KEYS.sendOfferRealty(),
    mutationFn: async (payload: Offer<RealtyCalc, RealtyCalcResultData>) => {
      const { data } = await calculatorsApi.sendOffer(payload);

      return data;
    },
  });
};

export const useTravelSendOffer = () => {
  return useMutation({
    mutationKey: CALCULATORS_QUERY_KEYS.sendOfferTravel(),
    mutationFn: async (payload: Offer<TravelCalc, TravelCalcResultData>) => {
      const { data } = await calculatorsApi.sendOffer(payload);

      return data;
    },
  });
};

export const useUploadCalcAttachments = () => {
  return useMutation({
    mutationKey: CALCULATORS_QUERY_KEYS.uploadCalcAttachments(),
    mutationFn: async (payload: FormData) => {
      const { data } = await calculatorsApi.uploadCalcAttachments(payload);

      return data;
    },
  });
};

export const useDownloadCardReader = () => {
  return useMutation({
    mutationKey: CALCULATORS_QUERY_KEYS.downloadCardReader(),
    mutationFn: async () => {
      const response = await calculatorsApi.downloadCardReader();
      openBlobFile(response, true);

      return response.data;
    },
  });
};
